import { DateOption, DateGrouping } from '../models';
import { FluentDate } from '.';

interface IDateOptions {
	today: DateOption;
	yesterday: DateOption;
	last7Days: DateOption;
	last30Days: DateOption;
	last90Days: DateOption;
	thisMonth: DateOption;
	lastMonth: DateOption;
	thisYear: DateOption;
	lastYear: DateOption;
}

interface IDatePreset {
	[key: string]: DateOption;
}

export class DatePicker {
	public static isValidMonthOption(option: DateOption): boolean {
		return DatePicker.monthOptions.toArray().some(m =>
			m.getDateRange().start.getTime() === option.getDateRange().start.getTime() &&
			m.getDateRange().end.getTime() === option.getDateRange().end.getTime());
	}

	private static get presets(): IDatePreset {
		return {
			today: {
				label: 'Today',
				group: DateGrouping.HOUR,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfDay().value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 0
			},
			yesterday: {
				label: 'Yesterday',
				group: DateGrouping.HOUR,
				getDateRange: () => {
					return {
						start: new FluentDate().subtractDays(1).startOfDay().value,
						end: new FluentDate().subtractDays(1).endOfDay().value
					};
				},
				listGroup: 0
			},
			last7Days: {
				label: 'Last 7 days',
				group: DateGrouping.DAY,
				getDateRange: () => {
					return {
						start: new FluentDate().subtractDays(7).startOfDay().value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 1
			},
			last30Days: {
				label: 'Last 30 days',
				group: DateGrouping.DAY,
				getDateRange: () => {
					return {
						start: new FluentDate().subtractDays(30).startOfDay().value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 1
			},
			last90Days: {
				label: 'Last 90 days',
				group: DateGrouping.DAY,
				getDateRange: () => {
					return {
						start: new FluentDate().subtractDays(90).startOfDay().value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 1
			},
			last3Months: {
				label: 'Last 3 months',
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfMonth().subtractMonths(2).value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 2
			},
			last6Months: {
				label: 'Last 6 months',
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfMonth().subtractMonths(5).value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 2
			},
			last12Months: {
				label: 'Last 12 months',
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfMonth().subtractMonths(11).value,
						end: new FluentDate().endOfDay().value
					};
				},
				listGroup: 2
			},
			thisMonth: {
				label: 'This month',
				group: DateGrouping.DAY,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfMonth().value,
						end: new FluentDate().endOfMonth().value
					};
				},
				listGroup: 2
			},
			lastMonth: {
				label: 'Last month',
				group: DateGrouping.DAY,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfMonth().subtractMonths(1).value,
						end: new FluentDate().startOfMonth().subtractMonths(1).endOfMonth().value
					};
				},
				listGroup: 2
			},
			thisYear: {
				label: 'This year',
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate().startOfYear().value,
						end: new FluentDate().endOfYear().value
					};
				},
				listGroup: 3
			},
			lastYear: {
				label: 'Last year',
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate().subtractYears(1).startOfYear().value,
						end: new FluentDate().subtractYears(1).endOfYear().value
					};
				},
				listGroup: 3
			}
		};
	}

	private static dateOptions: IDateOptions = {
		today: DatePicker.presets.today,
		yesterday: DatePicker.presets.yesterday,
		last7Days: DatePicker.presets.last7Days,
		last30Days: DatePicker.presets.last30Days,
		last90Days: DatePicker.presets.last90Days,
		thisMonth: DatePicker.presets.thisMonth,
		lastMonth: DatePicker.presets.lastMonth,
		thisYear: DatePicker.presets.thisYear,
		lastYear: DatePicker.presets.lastYear
	};

	public static get options() {
		return {
			...this.dateOptions,
			toArray: (): DateOption[] => Object.values(this.dateOptions)
		};
	}

	public static get monthOptions() {
		const options = {
			thisMonth: DatePicker.presets.thisMonth,
			lastMonth: DatePicker.presets.lastMonth,
			last3Months: DatePicker.presets.last3Months,
			last6Months: DatePicker.presets.last6Months,
			last12Months: DatePicker.presets.last12Months
		};

		const currentYear = new Date().getFullYear();

		for (let i = currentYear; i > currentYear - 3; i--) {
			options[i.toString() as keyof typeof options] = {
				label: i.toString(),
				group: DateGrouping.MONTH,
				getDateRange: () => {
					return {
						start: new FluentDate(new Date(i, 0, 1)).startOfYear().value,
						end: new FluentDate(new Date(i, 0, 1)).endOfYear().value
					};
				},
				listGroup: 3
			};
		}

		return {
			...options,
			toArray: (): DateOption[] => Object.values(options)
		};
	}
}
